import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { getGroupTicketDemands } from '../../../../api/endpoints/demands';
import type { GroupTicketDemand } from '../../../../api/types/demands';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { GroupTicketDemandDialog } from '../GroupTicketDemandDialog';

/** See BusDemandPage.tsx header comment for why this uses a plain MUI Table instead of
 *  @mui/x-data-grid (that package's typings don't parse under this project's pinned TS 4.9.5). */
export default function GroupTicketDemandPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<GroupTicketDemand | null>(null);

  const query = useQuery({
    queryKey: ['demands', 'groupTicket'],
    queryFn: () => getGroupTicketDemands(),
  });

  const rows: GroupTicketDemand[] = query.data?.groupTicketDemands ?? [];

  return (
    <Box>
      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Grup Bilet Talepleri</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Yeni Talep
        </Button>
      </Stack>

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}
      {query.isFetching && <CircularProgress size={24} sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Kalkış</TableCell>
              <TableCell>Varış</TableCell>
              <TableCell>Kişi Sayısı</TableCell>
              <TableCell>Gidiş Tarihi</TableCell>
              <TableCell>Dönüş Tarihi</TableCell>
              <TableCell>Havayolu Tercihi</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && !query.isFetching && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                    Kayıtlı talep bulunamadı.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <TableRow key={row.id} hover>
                <TableCell>{row.from ?? '—'}</TableCell>
                <TableCell>{row.to ?? '—'}</TableCell>
                <TableCell>{row.personCount ?? '—'}</TableCell>
                <TableCell>{row.depDate ?? '—'}</TableCell>
                <TableCell>{row.retDate ?? '—'}</TableCell>
                <TableCell>{(row.airlinePreference ?? []).join(', ') || '—'}</TableCell>
                <TableCell>
                  <Chip size="small" color={row.isApproved ? 'success' : 'default'} label={row.isApproved ? 'Onaylandı' : 'Beklemede'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => setEditing(row)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <GroupTicketDemandDialog open={createOpen} demand={null} onClose={() => setCreateOpen(false)} />
      <GroupTicketDemandDialog open={!!editing} demand={editing} onClose={() => setEditing(null)} />
    </Box>
  );
}
